// =============================================================================
// MOBILE HAMBURGER MENU COMPONENT
// Slide-in menu for mobile navigation with user info and grouped nav items
// =============================================================================

import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { mobileMenuItems, filterNavItems, groupBySection, type NavItem } from '../../config/navigation';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useDashboardSummary } from '../../hooks/useDashboardAlerts';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuButtonProps {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  badgeCount?: number;
  badgeVariant?: 'danger' | 'warning' | 'earth' | 'success' | 'info';
}

// -----------------------------------------------------------------------------
// Menu Button Subcomponent
// -----------------------------------------------------------------------------

function MenuButton({ item, isActive, onClick, badgeCount, badgeVariant = 'danger' }: MenuButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-4 py-3 rounded-xl
        transition-colors touch-manipulation min-h-touch
        ${isActive
          ? 'bg-primary-50 text-forest-800 font-medium'
          : 'text-forest-700 hover:bg-sage-100'
        }
      `}
    >
      <span className="text-xl w-7 text-center">{item.icon}</span>
      <span className="flex-1 text-left">{item.label}</span>
      {badgeCount !== undefined && badgeCount > 0 && (
        <Badge variant={badgeVariant} size="sm">
          {badgeCount}
        </Badge>
      )}
      {isActive && !badgeCount && (
        <span className="w-2 h-2 rounded-full bg-forest-600" />
      )}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Section Header Subcomponent
// -----------------------------------------------------------------------------

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="px-4 py-2 mt-4 first:mt-0">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-sage-500">
        {title}
      </h3>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isSuperAdmin } = useAuthStore();

  // Fetch dashboard summary for alert counts
  const { data: summary } = useDashboardSummary();

  // Get user roles and permissions for filtering
  const userRoles = user?.roles || [];
  const userPermissions = user?.permissions || [];

  // Get badge count for a nav item based on its badge type
  const getBadgeCount = (item: NavItem): number | undefined => {
    if (!item.badge || !summary) return undefined;

    switch (item.badge) {
      case 'urgent':
        return summary.workorders.urgent;
      case 'stalled':
        return summary.workorders.stalled;
      case 'readyForPickup':
        return summary.workorders.readyForPickup;
      case 'aging':
        return summary.clients.aging;
      case 'missedService':
        return summary.clients.missedService;
      case 'noNextVisit':
        return summary.clients.noNextVisit;
      case 'routeNotScheduled':
        return summary.admin.routeNotScheduled;
      case 'noRoute':
        return summary.admin.noRoute;
      case 'emailUnread':
        return summary.email?.unread;
      case 'alerts':
        return summary.grandTotal;
      default:
        return undefined;
    }
  };

  // Get badge variant based on badge type
  const getBadgeVariant = (item: NavItem): 'danger' | 'warning' | 'earth' | 'info' => {
    if (item.badge === 'urgent' || item.badge === 'missedService') return 'danger';
    if (item.badge === 'aging' || item.badge === 'routeNotScheduled' || item.badge === 'noNextVisit') return 'earth';
    if (item.badge === 'emailUnread') return 'info';
    return 'warning';
  };

  // Filter menu items based on user access
  const filteredItems = filterNavItems(
    mobileMenuItems,
    userRoles,
    userPermissions,
    isSuperAdmin
  );

  // Group items by section
  const groupedItems = groupBySection(filteredItems);

  // Handle navigation
  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    onClose();
  }, [navigate, onClose]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    await logout();
    onClose();
  }, [logout, onClose]);

  // Handle keyboard events - close on Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Get user initials for avatar
  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  // Check if path is active
  const isPathActive = (path: string) => location.pathname === path;

  // Section labels
  const sectionLabels: Record<string, string> = {
    main: 'Navigation',
    alerts: 'Alerts',
    tools: 'Tools',
    admin: 'Admin',
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-in panel */}
      <div
        className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white z-50
                   flex flex-col animate-slide-in-right shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header with user info */}
        <div className="bg-forest-800 text-white px-4 py-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {/* User avatar */}
              <div className="w-12 h-12 rounded-full bg-forest-600 flex items-center justify-center text-lg font-medium">
                {userInitials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name || 'User'}</p>
                <p className="text-sm text-forest-300 truncate">{user?.email}</p>
              </div>
            </div>
            {/* Close button */}
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full
                         hover:bg-forest-700 transition-colors touch-manipulation"
              aria-label="Close menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable content with nav sections */}
        <div className="flex-1 overflow-y-auto px-2 py-4">
          {/* Main navigation section */}
          {groupedItems.main.length > 0 && (
            <div>
              <SectionHeader title={sectionLabels.main} />
              <div className="space-y-1">
                {groupedItems.main.map((item) => (
                  <MenuButton
                    key={item.path}
                    item={item}
                    isActive={isPathActive(item.path)}
                    onClick={() => handleNavigate(item.path)}
                    badgeCount={getBadgeCount(item)}
                    badgeVariant={getBadgeVariant(item)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Alerts section */}
          {groupedItems.alerts.length > 0 && (
            <div>
              <SectionHeader title={sectionLabels.alerts} />
              <div className="space-y-1">
                {groupedItems.alerts.map((item) => (
                  <MenuButton
                    key={item.path}
                    item={item}
                    isActive={isPathActive(item.path)}
                    onClick={() => handleNavigate(item.path)}
                    badgeCount={getBadgeCount(item)}
                    badgeVariant={getBadgeVariant(item)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Tools section */}
          {groupedItems.tools.length > 0 && (
            <div>
              <SectionHeader title={sectionLabels.tools} />
              <div className="space-y-1">
                {groupedItems.tools.map((item) => (
                  <MenuButton
                    key={item.path}
                    item={item}
                    isActive={isPathActive(item.path)}
                    onClick={() => handleNavigate(item.path)}
                    badgeCount={getBadgeCount(item)}
                    badgeVariant={getBadgeVariant(item)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Admin section */}
          {groupedItems.admin.length > 0 && (
            <div>
              <SectionHeader title={sectionLabels.admin} />
              <div className="space-y-1">
                {groupedItems.admin.map((item) => (
                  <MenuButton
                    key={item.path}
                    item={item}
                    isActive={isPathActive(item.path)}
                    onClick={() => handleNavigate(item.path)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer with logout */}
        <div className="border-t border-sage-200 px-4 py-4">
          <Button
            variant="secondary"
            onClick={handleLogout}
            className="w-full"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Sign Out
          </Button>
        </div>
      </div>
    </>
  );
}
